import { useActivityCurrent, useActivityWaiting } from '@/store/activity-queue';
import { FAKE_DISCOUNT_ANIMATION } from '@/utils/pay/animation-config';
import { TRACK_ORDER_FROM } from '@/utils/tracker/pay';

import { resolveDiscountPhase } from './discount-phase';
import { useFakeDiscountStore } from './slice';

import type { TFakeDiscountPhase } from './discount-phase';

/** 本次会话是否已领取假优惠 */
export const useFakeDiscountClaimed = () => useFakeDiscountStore(state => state.claimed);

/** 收口动效是否待播放（领取后未被任何付费墙消费） */
export const useFakeDiscountClaimAnimationPending = () => useFakeDiscountStore(state => state.claimAnimationPending);

/** 队列中是否有待展示的假优惠任务（到墙拉取已返回、弹窗尚未展示）：假价格预展示的依据 */
export const useHasPendingFakeDiscount = () => useActivityWaiting().some(task => task.type === 'fake_discount');

type TFakeDiscountStoreState = ReturnType<typeof useFakeDiscountStore.getState>;

/** 命中商品 id：弹窗期间取承载墙注册值，领取后取领取瞬间快照，任务就位未展示时取预注册值 */
const resolveTargetProductId = (state: TFakeDiscountStoreState, popupVisible: boolean): string | undefined => {
    if (popupVisible) {
        return state.claimHostProductId || undefined;
    }
    if (state.claimed) {
        return state.claimedProductId || undefined;
    }
    return state.pendingTargetProductId || undefined;
};

/** 价格翻滚态：已领取且按配置无需等金币到位 / 金币已到位 */
const resolvePriceRolling = (state: TFakeDiscountStoreState) =>
    state.claimed && (FAKE_DISCOUNT_ANIMATION.priceRoll.start === 'with-coin-fly' || state.coinFlyDone);

/** 价格是否处于翻滚态：已领取且按配置无需等金币到位 / 金币已到位 */
export const useFakeDiscountPriceRolling = () => useFakeDiscountStore(resolvePriceRolling);

/** 假优惠当前命中的商品 id：弹窗期间取承载墙注册的命中商品，领取后取领取瞬间快照；未命中为 undefined */
export const useFakeDiscountTargetProductId = (): string | undefined => {
    const popupVisible = useActivityCurrent()?.type === 'fake_discount';
    return useFakeDiscountStore(state => resolveTargetProductId(state, popupVisible));
};

/**
 * 单个商品卡的假优惠阶段（未命中返回 undefined）。
 * 单一 selector 直接产出阶段值：未命中卡在假优惠状态变化时返回值不变，不触发重渲染。
 */
export const useFakeDiscountPhase = (productId: string): TFakeDiscountPhase | undefined => {
    const popupVisible = useActivityCurrent()?.type === 'fake_discount';
    return useFakeDiscountStore(state =>
        resolveDiscountPhase(productId, resolveTargetProductId(state, popupVisible), resolvePriceRolling(state))
    );
};

/**
 * 假优惠归因：会话内已领取即返回埋点 order_from 取值 fake_offer，否则返回 undefined。
 * 会话级口径：领取后购买任意档位都算假优惠转化——领取后轮播自动切档会改变选中商品，
 * 若按商品精确匹配会大面积丢归因（BI_TRACK_ORDER_FROM_FIELD）。
 * 领取时无命中商品（claimedProductId 为空）不归因。非响应式，供埋点组装时快照读取。
 */
export function resolveFakeOfferClaimedOrderFrom(): string | undefined {
    const { claimed, claimedProductId } = useFakeDiscountStore.getState();
    if (!claimed || !claimedProductId) {
        return undefined;
    }
    return TRACK_ORDER_FROM.FAKE_OFFER;
}
